import {
  CompanyFactKey,
  CompanyFactSchemaVersion,
  ConflictStrategy,
  type FactKeyConfig,
} from '../core'

export enum AttirePolicyOption {
  CLOSED_TOED_SHOES = 'CLOSED_TOED_SHOES',
  STEEL_TOED_BOOTS = 'STEEL_TOED_BOOTS',
  SLIP_RESISTANT_SHOES = 'SLIP_RESISTANT_SHOES',
  SHIRT_WITH_SLEEVES = 'SHIRT_WITH_SLEEVES',
  LONG_SLEEVE_SHIRTS = 'LONG_SLEEVE_SHIRTS',
  LONG_PANTS = 'LONG_PANTS',
  NO_HOODIES = 'NO_HOODIES',
  NO_OFFENSIVE_OR_GRAPHIC_CLOTHING = 'NO_OFFENSIVE_OR_GRAPHIC_CLOTHING',
  NO_JEWELRY = 'NO_JEWELRY',
  HAIR_RESTRAINTS_REQUIRED = 'HAIR_RESTRAINTS_REQUIRED',
  HIGH_VISIBILITY_VEST = 'HIGH_VISIBILITY_VEST',
}

export enum AttendancePolicyOption {
  STANDARD = 'STANDARD',
  STRICT = 'STRICT',
}

export enum ReturnPolicyOption {
  ALLOWED = 'ALLOWED',
  ALLOWED_WITH_RESTRICTIONS = 'ALLOWED_WITH_RESTRICTIONS',
  ALLOWED_EXCEPT_BLOCKS = 'ALLOWED_EXCEPT_BLOCKS',
  HAS_DNR_LIST = 'HAS_DNR_LIST',
  NOT_ALLOWED = 'NOT_ALLOWED',
  OTHER = 'OTHER',
}

export enum OvertimeVoluntaryOption {
  VOLUNTARY = 'VOLUNTARY',
  MANDATORY = 'MANDATORY',
}

export const WORK_POLICIES_KEY_CONFIG: Partial<
  Record<CompanyFactKey, FactKeyConfig>
> = {
  [CompanyFactKey.WORK_POLICIES_ATTIRE_POLICY]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_V1,
    displayName: 'Attire / PPE Policy',
    options: Object.values(AttirePolicyOption),
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
  },
  [CompanyFactKey.WORK_POLICIES_ATTENDANCE_POLICY]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_V1,
    displayName: 'Attendance Policy',
    options: Object.values(AttendancePolicyOption),
  },
  [CompanyFactKey.WORK_POLICIES_CALL_OUT_POLICY]: {
    schemaVersion: CompanyFactSchemaVersion.CALL_OUT_LINE_V1,
    displayName: 'Call-Off Procedures',
  },
  [CompanyFactKey.WORK_POLICIES_RETURN_POLICY]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1,
    displayName: 'Return Policy',
    options: Object.values(ReturnPolicyOption),
  },
  [CompanyFactKey.WORK_POLICIES_HOLIDAYS]: {
    schemaVersion: CompanyFactSchemaVersion.HOLIDAY_V1,
    displayName: 'Holidays',
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
  },
  [CompanyFactKey.WORK_POLICIES_COACHING_NOTES]: {
    schemaVersion: CompanyFactSchemaVersion.TEXT_V3,
    displayName: 'Coaching Notes',
    conflictStrategy: ConflictStrategy.APPEND,
    purposeTooltip:
      'Behavioral coaching nudges supervisors use to help workers avoid termination — informal site-specific norms (e.g., "no phones on the floor", "don\'t congregate near the entrance"). Distinct from Attendance Policy (formal violations) and from general Notes.',
  },
  [CompanyFactKey.WORK_POLICIES_OT_EXPECTED]: {
    schemaVersion: CompanyFactSchemaVersion.YES_NO_WITH_NOTES_V1,
    displayName: 'OT Expected',
    purposeTooltip:
      'Whether associates can expect overtime at this company. Use notes for context (e.g., "peak season only", "1–2 hrs most days").',
  },
  [CompanyFactKey.WORK_POLICIES_OT_VOLUNTARY_MANDATORY]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1,
    displayName: 'OT Voluntary or Mandatory',
    options: Object.values(OvertimeVoluntaryOption),
  },
  [CompanyFactKey.WORK_POLICIES_NOTES]: {
    schemaVersion: CompanyFactSchemaVersion.TEXT_V3,
    displayName: 'Notes',
    conflictStrategy: ConflictStrategy.APPEND,
  },
}
